<template>
  <v-row no-gutters>
    <v-col cols="8" class="mb-6 d-flex align-center">
      <h3 class="header__title--main">
        {{ $t('message.navDrawer.bottomActions.externalPlays') }}
      </h3>
      <LearnMoreBtn
        :onClickBtn="handleGoToExternalArticles"
        customClass="mr-2 ml-1"
      />
      <base-btn
        class="nowrap"
        noBorderRadius="right"
        size="xsmall"
        :type="isCurrentCycle ? 'primary' : 'secondary'"
        @click="isCurrentCycle = true"
        >{{ $t('message.pages.analytics.actualCicle') }}</base-btn
      >
      <base-btn
        class="mr-3"
        noBorderRadius="left"
        :type="!isCurrentCycle ? 'primary' : 'secondary'"
        size="xsmall"
        @click="isCurrentCycle = !true"
        >{{ $t('message.pages.analytics.period') }}</base-btn
      >
      <span class="currentCycle__date-range" v-show="currentShowingDates">
        {{ currentShowingDates[0] }} - {{ currentShowingDates[1] }}
      </span>
    </v-col>
    <v-col cols="4" class="d-flex justify-end" v-if="!isCurrentCycle">
      <div class="used-text__container">
        <div class="d-flex flex-column mr-5 used-text__inner-container">
          <span class="primary--text dataSum--text">
            {{ dataSum }}
          </span>
          <span class="used-gray-text text-end">
            {{ $t('message.pages.analytics.used') }}
          </span>
        </div>
      </div>
    </v-col>
    <v-col cols="12" class="px-0">
      <v-col cols="12" class="px-0" v-if="!noVideos">
        <v-card
          v-if="loading"
          flat
          height="250px"
          class="d-flex flex-row align-center justify-center"
        >
          <loading-logo :value="200" />
        </v-card>
        <v-card
          class="d-flex flex-row align-center justify-center"
          flat
          height="250px"
          width="100%"
          v-else-if="fetchError"
        >
          <ad-block-error-message
            v-if="isAdBlockEnabled"
            :isLoading="loading"
            @handleFetchData="fetchData"
          />
          <v-row
            v-else
            class="flex-column"
            justify="center"
            align="center"
            no-gutters
            style="min-height: 250px"
          >
            <v-card flat height="30" class="mb-4">
              <span class="footerText align-text d-block">
                {{ errors.shownErrMsg }}
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon size="18" v-bind="attrs" v-on="on">
                      mdi-help-circle
                    </v-icon>
                  </template>
                  <v-container
                    class="text-body-2 pa-0"
                    style="max-width: 230px"
                  >
                    {{ errors.tooltipErrMsg }}
                  </v-container>
                </v-tooltip>
              </span>
            </v-card>
            <v-btn color="primary" class="ml-3" @click="fetchData">{{
              $t('message.pages.analytics.tryAgain')
            }}</v-btn>
          </v-row>
        </v-card>
        <div class="relative-container" v-else-if="!loading && !noVideos">
          <progress-bar
            v-if="planInfo.limit && isCurrentCycle"
            isAnalyticsPage="true"
            :items="progressBarItems"
            :totalLength="handleKamount(planInfo.limit.play_plan)"
            :customHeight="16"
            class="mt-3 traffic-modal-bar mb-16 pb-3 px-4"
            :isTrafficModal="false"
          />
          <apexchart
            :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
            type="area"
            height="250"
            :options="options"
            :series="options.series"
          ></apexchart>
        </div>
      </v-col>
      <v-card
        class="mt-1 d-flex align-center"
        flat
        width="100%"
        height="48px"
        v-if="allTableData.length"
        style="border-bottom: 1px solid #e6e8f8; border-radius: 0px"
      >
        <v-row
          no-gutters
          v-if="!loading"
          class="color: var(--v-blueMarine-base)"
        >
          <v-col class="d-flex align-center justify-start">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderByName">
                <span class="caption pl-4" style="height: 24px">
                  <b>{{ $t('message.video') }}</b>
                </span>
                <v-icon v-if="hover && !nameOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && nameOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && nameOrder === 'asc'">mdi-menu-up</v-icon>
              </v-row>
            </v-hover>
          </v-col>
          <v-col class="d-flex align-center" height="45px" flat width="138px">
            <v-hover v-slot="{ hover }">
              <v-row no-gutters @click="handleOrderByValue">
                <span class="caption" style="height: 24px">
                  <b>{{
                    $t('message.navDrawer.bottomActions.externalPlays')
                  }}</b>
                </span>
                <v-icon v-if="hover && !valueOrder" color="grey lighten-1"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && valueOrder === 'desc'"
                  >mdi-menu-down</v-icon
                >
                <v-icon v-if="hover && valueOrder === 'asc'"
                  >mdi-menu-up</v-icon
                >
              </v-row>
            </v-hover>
          </v-col>
        </v-row>
      </v-card>
      <v-col cols="12" class="pa-0">
        <v-row no-gutters>
          <v-col cols="12" v-if="loading">
            <v-skeleton-loader type="table-tbody" />
          </v-col>
          <vue-virtual-table
            v-else-if="allTableData.length"
            class="analyticsTable"
            :class="{ virtualTable: $vuetify.theme.dark }"
            :data="allTableData"
            :config="headers"
            :height="tableHeight"
            :itemHeight="48"
            :minWidth="450"
          >
            <template slot-scope="scope" slot="titleCommon">
              <v-col
                class="d-flex align-center justify-start pa-0"
                style="padding-left: 15px !important"
              >
                <span
                  :title="getRowTitle(scope.row)"
                  class="text-truncate body-2"
                  style="max-width: 100%"
                  >{{ getRowTitle(scope.row) }}</span
                >
                <learn-more-btn
                  v-if="scope.row.status === 'DELETING'"
                  :customTooltipMessage="
                    $t('message.pages.analytics.videoInTrash')
                  "
                  :onClickBtn="
                    () => {
                      $router.push({ name: 'Trash' })
                    }
                  "
                  customStyle="margin-left: 3px;"
                  customClass="analytics__learnMore--button"
                />
                <learn-more-btn
                  v-else-if="scope.row.deleted"
                  :customTooltipMessage="
                    $t('message.pages.analytics.videoDeletedFromTrash')
                  "
                  customStyle="margin-left: 3px;"
                  customClass="analytics__learnMore--button"
                />
              </v-col>
            </template>
            <template slot-scope="scope" slot="valueCommon">
              <v-row no-gutters>
                <span class="body-2">{{
                  handleKamount(scope.row.analyticsData.play)
                }}</span>
              </v-row>
            </template>
          </vue-virtual-table>
          <empty-state v-else class="mb-16 pb-16" />
        </v-row>
      </v-col>
    </v-col>
  </v-row>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  format,
  addHours,
  subHours,
  addDays,
  differenceInDays,
  isSameDay,
} from 'date-fns'
import LoadingLogo from '@/components/LoadingLogo.vue'
import { mapState } from 'vuex'
import VueVirtualTable from 'vue-virtual-table'
import analyticsApi from '@/services/newAnalyticsApi'
import i18n from '@/translations/lang'
import ProgressBar from '@/components/ProgressBar2.vue'
import EmptyState from './EmptyState.vue'
import LearnMoreBtn from '@/components/LearnMoreBtn.vue'
import externalSourceVideoMixin from '@/utils/externalSourceVideo.mixin.js'

export default {
  name: 'Plays',
  components: {
    apexchart: VueApexCharts,
    LoadingLogo,
    VueVirtualTable,
    ProgressBar,
    EmptyState,
    LearnMoreBtn,
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  mixins: [externalSourceVideoMixin],
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
  },
  data() {
    const _this = this
    return {
      articleLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funciona-o-recurso-de-videos-externos-1vel7ty/',
        en: 'https://help.pandavideo.com/en-us/article/how-does-the-external-videos-feature-works-187j22s/',
      },
      usedPlays: 0,
      isLanguageChanging: false,
      isCurrentCycle: true,
      loading: true,
      allTableData: [],
      nameOrder: 'asc',
      valueOrder: 'asc',
      headers: [
        {
          prop: '_action',
          name: i18n.t('message.video'),
          actionName: 'titleCommon',
        },
        {
          prop: '_action',
          name: i18n.t('message.navDrawer.bottomActions.externalPlays'),
          actionName: 'valueCommon',
        },
      ],
      fetchError: false,
      options: {
        colors: ['#3F55EE'],
        theme: _this.$vuetify.theme ? _this.$vuetify.theme : 'light',
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            opacityFrom: 0.5,
            opacityTo: 0.5,
            shadeIntensity: 1,
            stops: [50, 100],
          },
        },
        series: [
          {
            name: i18n.t('message.navDrawer.bottomActions.externalPlays'),
            data: [],
          },
        ],
        chart: {
          toolbar: false,
          events: {
            mounted: (chart) => {
              chart.windowResizeHandler()
            },
          },
          type: 'area',
          zoom: {
            enabled: false,
          },
          offsetX: 0,
          offsetY: 0,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        labels: [],
        xaxis: {
          type: 'numeric',
          tickAmount: 6,
          labels: {
            style: {
              colors: '#9E9E9E',
            },
            formatter(val) {
              return format(new Date(val), 'dd/MM')
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#9E9E9E',
            },
            formatter: function (value) {
              if (value < 999) value = Number.parseInt(value)
              else if (value > 999 && value % 1 != 0)
                value = Number(value.toFixed(1))

              return `${_this.handleKamount(value)} plays`
            },
          },
        },
        tooltip: {
          theme: 'dark',
        },
      },
      initialAllTableData: [],
      noVideos: false,
      errors: {
        shownErrMsg: '',
        tooltipErrMsg: '',
      },
    }
  },
  computed: {
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
      planInfo: (state) => state.user.userPlanInfo,
    }),
    dataSum() {
      let sum = 0
      this.allTableData.map(({ analyticsData }) => {
        sum += analyticsData.play
      })

      return this.handleKamount(sum)
    },
    currentShowingDates() {
      const datePattern = this.getDatePattern()
      let startDate
      let endDate

      if (this.isCurrentCycle) {
        if (this.planInfo?.limit?.start_period) {
          startDate = this.planInfo?.limit?.start_period
          endDate = this.planInfo?.limit?.end_period
        }
      } else {
        startDate = this.dateRange[0]
        endDate = this.dateRange[1]
      }

      if (startDate && endDate) {
        startDate = format(this.getUTCDate(startDate), datePattern)
        endDate = format(this.getUTCDate(endDate), datePattern)

        return [startDate, endDate]
      }
      return false
    },
    tableHeight() {
      if (this.allTableData.length < 5) {
        return 288
      } else if (this.allTableData.length <= 100) {
        return (this.allTableData.length + 1) * 48 + 5
      } else {
        return 4805
      }
    },
    shouldHideProgressBar() {
      return Boolean(this.selectedVideos.length)
    },
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
    progressBarItems() {
      return this.progressBarItemsFull
    },
    progressBarItemsFull() {
      const { used } = this.getFullProgressBarInfo()

      return [
        {
          category: i18n.t('message.progressBar.usedWord'),
          percentage: used.percentage,
          length: this.handleKamount(used.used),
        },
      ]
    },
    currentDateRange() {
      if (this.isCurrentCycle && this.planInfo?.limit) {
        return [
          new Date(this.planInfo.limit.start_period),
          new Date(this.planInfo.limit.end_period),
        ]
      }
      return this.dateRange
    },
    selectedVideos() {
      const { selectedVideos } = this.componentData
      return selectedVideos
    },
    dateRange() {
      const { dateRange } = this.componentData
      return dateRange
    },
  },
  watch: {
    isCurrentCycle: function () {
      if (this.selectedVideos.length) {
        this.fetchFilteredData()
      } else {
        this.fetchData()
      }
    },
    dateRange: function (val) {
      if (!val[0] || !val[1] || this.loading) return
      if (this.isCurrentCycle && !this.isLanguageChanging) {
        this.isCurrentCycle = false
        return
      }
      if (this.selectedVideos.length) {
        this.fetchFilteredData()
      } else {
        this.fetchData()
      }
    },
    selectedVideos: async function (value) {
      if (this.loading) return
      if (value.length) {
        this.fetchFilteredData()
      } else {
        if (this.initialAllTableData.length) {
          this.loading = true
          this.allTableData = this.initialAllTableData
          await this.fetchGeneralChartGraphic()
          setTimeout(() => {
            this.loading = false
          }, 1000)
        } else {
          this.fetchData()
        }
      }
    },
    planInfo: function () {
      this.handleSetProgressBarData()
    },
  },
  async mounted() {
    if (!this.planInfo?.limit) {
      this.$root.$on('onFinishLoadPlanInfo', () => {
        this.afterMounted()
      })
    } else {
      this.afterMounted()
    }
    this.$root.$on('isLanguageChanging', (val) => {
      this.isLanguageChanging = val
    })
  },
  created() {
    this.$root.$emit('initDexie')
  },
  beforeDestroy() {},
  methods: {
    afterMounted() {
      if (this.selectedVideos.length) {
        this.fetchFilteredData()
      } else {
        this.fetchData()
      }
    },
    handleGoToExternalArticles() {
      const articleLink =
        this.articleLinks[this.$i18next.resolvedLanguage] ??
        this.articleLinks['en']
      window.open(articleLink, '_blank')
    },
    getRowTitle(row) {
      if (row.title) return row.title
      if (row.video_external_id) return row.video_external_id
      return ''
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    getDatePattern() {
      const locale = this.getLocale()
      if (locale === 'pt-BR') return 'dd/MM/yyyy'
      return 'MM/dd/yyyy'
    },
    getFullProgressBarInfo() {
      let usedPlays = this.usedPlays
      let usedPercentage = this.getUsedPercentage(usedPlays)

      return {
        used: {
          used: usedPlays,
          percentage: usedPercentage,
        },
      }
    },
    getUsedPercentage(usedLength) {
      return (
        (usedLength /
          (this.planInfo.limit ? this.planInfo.limit.play_plan : 0)) *
        100
      )
    },
    getUTCDate(incomingDate) {
      let date = new Date(incomingDate)
      const timezone = date.getTimezoneOffset() / 60
      if (timezone < 0) {
        date = subHours(
          new Date(incomingDate),
          new Date(incomingDate).getTimezoneOffset() / 60
        )
      } else {
        date = addHours(
          new Date(incomingDate),
          new Date(incomingDate).getTimezoneOffset() / 60
        )
      }
      return date
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc') {
        this.nameOrder = 'asc'
      } else {
        this.nameOrder = 'desc'
      }
      if (this.nameOrder === 'asc') {
        this.allTableData.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.allTableData.sort(
          (a, b) =>
            -a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
    },
    handleOrderByValue() {
      if (!this.valueOrder || this.valueOrder === 'desc') {
        this.valueOrder = 'asc'
      } else {
        this.valueOrder = 'desc'
      }
      if (this.valueOrder === 'asc') {
        this.allTableData.sort(
          (a, b) => a.analyticsData.play - b.analyticsData.play
        )
      }
      if (this.valueOrder === 'desc') {
        this.allTableData.sort(
          (a, b) => b.analyticsData.play - a.analyticsData.play
        )
      }
    },
    async fetchData() {
      this.clearErrors()
      this.loading = true
      this.noVideos = false
      this.allTableData = []
      try {
        const [start_date, end_date] = this.getFormattedDates(
          this.currentDateRange
        )
        const data = await analyticsApi.getRanking(start_date, end_date)
        const aggregatedRanking = await this.getAggregatedRanking(data.data)

        if (!Object.keys(aggregatedRanking).length) {
          this.noVideos = true
          this.loading = false
          return
        }

        data.data.forEach(({ video_external_id, play }) => {
          if (video_external_id) {
            if (aggregatedRanking[video_external_id]) {
              aggregatedRanking[video_external_id].analyticsData = {
                play: Number(play),
              }
            } else {
              aggregatedRanking[video_external_id] = {
                deleted: true,
                video_external_id: video_external_id,
                analyticsData: {
                  play: Number(play),
                },
              }
            }
          }
        })

        this.allTableData = [...Object.values(aggregatedRanking)]
        this.initialAllTableData = [...this.allTableData]
        this.fetchError = false
      } catch (err) {
        this.handleFetchError(err)
      }
      await this.fetchGeneralChartGraphic()
      this.handleSetProgressBarData()
      this.loading = false
    },
    async aggregateTableData(
      videosPromises,
      aggregatedRanking,
      general = false
    ) {
      const videosValue = await Promise.all(videosPromises)
      videosValue.map((axiosObject, index) => {
        let searchIndex
        if (general) {
          const url = new URL(axiosObject.request.responseURL)
          const videoId = url.searchParams.get('video_id')
          searchIndex = videoId
        } else {
          searchIndex = index
        }
        aggregatedRanking[searchIndex].analyticsData =
          axiosObject.data.views_data
        aggregatedRanking[searchIndex].analyticsData.play =
          axiosObject.data.total.play
      })

      return aggregatedRanking
    },
    getVideoValuePromises(keys) {
      keys = keys.map((id) => {
        if (id?.id) id = id.id
        return analyticsApi.getSingleVideoAnalytics(
          id,
          this.getFormattedDates(this.currentDateRange)[0],
          this.getFormattedDates(this.currentDateRange)[1]
        )
      })
      return keys
    },
    async fetchFilteredData() {
      this.loading = true
      this.noVideos = false
      const videosPromises = this.getVideoValuePromises(this.selectedVideos)
      const selectedVideos = await this.aggregateTableData(
        videosPromises,
        this.selectedVideos
      )
      this.allTableData = [...Object.values(selectedVideos)]
      this.fetchSpecificChartGraphic()
      this.handleSetProgressBarData()
      setTimeout(() => {
        this.loading = false
      }, 1000)
    },
    handleSetProgressBarData() {
      if (this.planInfo) {
        this.usedPlays = this.planInfo?.limit?.play
      }
    },
    async fetchGeneralChartGraphic() {
      try {
        const [start_date, end_date] = this.getFormattedDates(
          this.currentDateRange
        )
        const { data } = await analyticsApi.getGeneralExternalPlays(
          start_date,
          end_date
        )
        data.valueData = this.getCuttedOffValueData(data)

        const valueDataArray = data.valueData.map(({ p }) => p)
        const categories = data.map((item) => {
          const date = this.getUTCDate(item.t)
          return date.getTime()
        })

        this.options.series[0].data = valueDataArray
        this.options.labels = categories
      } catch (err) {
        this.handleFetchError(err)
      }
    },
    async fetchSpecificChartGraphic() {
      try {
        let valueDataArray = []
        const categories = []
        this.allTableData.map(({ analyticsData }) => {
          const objValues = Object.keys(analyticsData)
          objValues.map((date) => {
            if (date !== 'play') {
              const categoryDate = this.getUTCDate(date).getTime()
              const playCount = analyticsData[date].play
              const sameDayCategory = categories.filter((timestamp) =>
                isSameDay(new Date(timestamp), new Date(categoryDate))
              )
              if (!sameDayCategory.length) {
                valueDataArray.push(playCount)
                categories.push(categoryDate)
              } else {
                const categoryIndex = categories.indexOf(sameDayCategory[0])
                valueDataArray[categoryIndex] = valueDataArray[categoryIndex]
                  ? valueDataArray[categoryIndex] + playCount
                  : playCount
              }
            }
          })
        })
        const diffDays = differenceInDays(
          this.getUTCDate(format(this.currentDateRange[1], 'yyyy-MM-dd')),
          this.getUTCDate(format(this.currentDateRange[0], 'yyyy-MM-dd'))
        )
        if (
          (!valueDataArray.length && !categories.length) ||
          diffDays > categories.length
        ) {
          for (let i = categories.length - 1; i <= diffDays; i++) {
            const date = addDays(this.getUTCDate(this.currentDateRange[0]), i)
            let newDateIndex = -1
            categories.forEach((categoryDate, index) => {
              if (isSameDay(new Date(categoryDate), date)) {
                newDateIndex = index
              }
            })
            if (newDateIndex !== -1) {
              const [newDateValue, newValue] = [
                categories[newDateIndex],
                valueDataArray[newDateIndex],
              ]
              categories.splice(newDateIndex, 1)
              categories.push(newDateValue)
              valueDataArray.splice(newDateIndex, 1)
              valueDataArray.push(newValue)
            } else {
              valueDataArray.push(0)
              categories.push(date.getTime())
            }
          }
        }
        valueDataArray = this.getCuttedOffValueData(valueDataArray)
        this.options.series[0].data = valueDataArray
        this.options.labels = categories
      } catch (err) {
        this.handleFetchError(err)
      }
    },
    getCuttedOffValueData(valueArray) {
      if (this.isCurrentCycle) {
        const diffDays =
          differenceInDays(new Date(), this.currentDateRange[0]) + 1
        valueArray = valueArray.slice(0, diffDays)
      }
      return valueArray
    },
    getFormattedDates(dates) {
      return [format(dates[0], 'yyyy-MM-dd'), format(dates[1], 'yyyy-MM-dd')]
    },
    async getAggregatedRanking(data) {
      const aggregatedVideos = {}
      await Promise.all(
        data.map(async ({ video_external_id }) => {
          if (
            video_external_id &&
            video_external_id.indexOf('65787465') === 0
          ) {
            const videoInfo = await window.db.videos
              .where({
                video_external_id: video_external_id,
              })
              .toArray()
            if (videoInfo[0]?.video_external_id) {
              aggregatedVideos[videoInfo[0].video_external_id] = videoInfo[0]
            }
          }
        })
      )
      return aggregatedVideos
    },
    handleFetchError(err) {
      this.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err.response.data.errMsg ??
        err.response.data.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
*::v-deep {
  .page-view__content {
    width: 100%;
  }
}
.currentCycle__date-range {
  color: var(--v-blueMarine-base);
  font-size: 14px;
}
.dataSum--text {
  font-size: 24px;
  font-weight: 500;
}
.used-gray-text {
  color: #757575;
}
.used-text__container {
  position: relative;
  width: inherit;
}
.used-text__inner-container {
  position: absolute;
  right: 0px;
}
</style>

<style>
.analyticsTable .item-container .item-cell {
  border-color: #e6e8f8 !important;
}

.subtitle--gray {
  color: #757575;
}

.relative-container {
  width: 100%;
  position: relative;
  display: inline-block;
}
.nowrap {
  white-space: nowrap;
}
</style>
